import { Page, DurationOption, PlanFeature, PlanDetails, LandingPagePlan, Subscription, CreateSubscriptionInput, UpdateSubscriptionInput, SubscriptionFull, SubscriptionPayment, ApiResponse } from "./types";
import { axiosInstance as defaultAxiosInstance } from "./utils/axiosInterceptor";
import { AxiosInstance } from "axios";
import { QueryClient } from "@tanstack/react-query";

export const PLAN_CACHE_KEY = "plans";
export const SUBSCRIPTION_CACHE_KEY = "subscriptions";

let axiosInstance: AxiosInstance = defaultAxiosInstance;

export function setAxiosInstance(instance: AxiosInstance) {
  axiosInstance = instance;
}

/**
 * Create a plan
 *
 * @method POST
 * @param data - Plan data
 * @returns Promise resolving to created plan
 */
export async function createPlan(data: Omit<PlanDetails, "is_active">) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}`, data);
  return response.data as ApiResponse<PlanDetails>;
}

/**
 * Get all plans with optional filtering
 *
 * @method GET
 * @param filters - Optional filters (plan_type, is_active)
 * @returns Promise resolving to array of plans
 */
export async function getPlans(filters?: { plan_type?: string; is_active?: boolean }) {
  const params = new URLSearchParams();
  if (filters?.plan_type) params.append("plan_type", filters.plan_type);
  if (filters?.is_active !== undefined) params.append("is_active", filters.is_active.toString());

  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}?${params.toString()}`);
  return response.data as ApiResponse<PlanDetails[]>;
}

/**
 * Get all plans with pagination
 *
 * @method GET
 * @param filters - Optional filters (plan_type, is_active)
 * @param page - Page number (default: 0)
 * @param size - Page size (default: 10)
 * @returns Promise resolving to paginated plans
 */
export async function getPlansPaged(
  filters?: { plan_type?: string; is_active?: boolean },
  page: number = 0,
  size: number = 10
) {
  console.log("getPlansPaged called with:", { filters, page, size });

  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());
  if (filters?.plan_type) params.append("plan_type", filters.plan_type);
  if (filters?.is_active !== undefined) params.append("is_active", filters.is_active.toString());

  const url = `${PLAN_CACHE_KEY}/paged?${params.toString()}`;
  console.log("Making request to:", url);

  try {
    const response = await axiosInstance.get(url);
    console.log("Response received:", response.data);
    return response.data as Page<PlanDetails>;
  } catch (error) {
    console.error("Error in getPlansPaged:", error);
    throw error;
  }
}

/**
 * Get active plans only
 *
 * @method GET
 * @returns Promise resolving to array of active plans
 */
export async function getActivePlans() {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/active`);
  return response.data as ApiResponse<PlanDetails[]>;
}

/**
 * Get active plans with pagination
 *
 * @method GET
 * @param page - Page number (default: 0)
 * @param size - Page size (default: 10)
 * @returns Promise resolving to paginated active plans
 */
export async function getActivePlansPaged(page: number = 0, size: number = 10) {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());

  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/active/paged?${params.toString()}`);
  return response.data as Page<PlanDetails>;
}

// Add the landing page function
export async function getLandingPagePlans(planIds: string[]): Promise<LandingPagePlan[]> {
  try {
    const response = await axiosInstance.get("/plans/landing-page", {
      params: { planIds },
    });

    const apiResponse = response.data as ApiResponse<LandingPagePlan[]>;
    return apiResponse.data;
  } catch (error) {
    console.error("Error fetching landing page plans:", error);
    throw error;
  }
}

/**
 * Get plan by ID with full details
 *
 * @method GET
 * @param id - Plan ID
 * @returns Promise resolving to plan details
 */
export async function getPlanById(id: string) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/${id}`);
  return response.data as ApiResponse<PlanDetails>;
}

/**
 * Update a plan
 *
 * @method PUT
 * @param id - Plan ID
 * @param data - Updated plan data
 * @returns Promise resolving to success message
 */
export async function updatePlan(id: string, data: Partial<PlanDetails>) {
  const response = await axiosInstance.put(`${PLAN_CACHE_KEY}/${id}`, data);
  return response.data as ApiResponse<null>;
}

/**
 * Delete a plan
 *
 * @method DELETE
 * @param id - Plan ID
 * @returns Promise resolving to success message
 */
export async function deletePlan(id: string) {
  const response = await axiosInstance.delete(`${PLAN_CACHE_KEY}/${id}`);
  return response.data as ApiResponse<null>;
}

/**
 * Copy/duplicate a plan (and its features + durations).
 *
 * @method POST
 * @param sourceId - ID of the plan to copy
 * @param data - Optional payload; if `newId` is omitted the server derives
 *               `<sourceId>-copy` (or `-copy-2` ... `-copy-9`).
 * @returns Promise resolving to `{ id: string }` of the newly created plan.
 *          Note: the axios interceptor unwraps the `{ success, data, message }`
 *          envelope, so the resolved value is the inner payload.
 */
export async function copyPlan(sourceId: string, data?: { newId?: string }) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}/copy/${sourceId}`, data ?? {});
  return response.data as { id: string };
}

/**
 * Get plan durations
 *
 * @method GET
 * @param planId - Plan ID
 * @returns Promise resolving to array of durations
 */
export async function getPlanDurations(planId: string) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/${planId}/durations`);
  return response.data as ApiResponse<DurationOption[]>;
}

/**
 * Create plan duration
 *
 * @method POST
 * @param planId - Plan ID
 * @param data - Duration data
 * @returns Promise resolving to created duration
 */
export async function createPlanDuration(planId: string, data: Omit<DurationOption, "id">) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}/${planId}/durations`, data);
  return response.data as ApiResponse<{ insertId: number }>;
}

/**
 * Update plan duration
 *
 * @method PUT
 * @param durationId - Duration ID
 * @param data - Updated duration data
 * @returns Promise resolving to success message
 */
export async function updatePlanDuration(durationId: number, data: Partial<DurationOption>) {
  const response = await axiosInstance.put(`${PLAN_CACHE_KEY}/durations/${durationId}`, data);
  return response.data as ApiResponse<null>;
}

/**
 * Delete plan duration
 *
 * @method DELETE
 * @param durationId - Duration ID
 * @returns Promise resolving to success message
 */
export async function deletePlanDuration(durationId: number) {
  const response = await axiosInstance.delete(`${PLAN_CACHE_KEY}/durations/${durationId}`);
  return response.data as ApiResponse<null>;
}

/**
 * Get plan features
 *
 * @method GET
 * @param planId - Plan ID
 * @returns Promise resolving to array of features
 */
export async function getPlanFeatures(planId: string) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/${planId}/features`);
  return response.data as ApiResponse<PlanFeature[]>;
}

/**
 * Create plan feature
 *
 * @method POST
 * @param planId - Plan ID
 * @param data - Feature data
 * @returns Promise resolving to created feature
 */
export async function createPlanFeature(planId: string, data: Omit<PlanFeature, "id" | "plan_id">) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}/${planId}/features`, data);
  return response.data as ApiResponse<{ insertId: number }>;
}

/**
 * Update plan feature
 *
 * @method PUT
 * @param featureId - Feature ID
 * @param data - Updated feature data
 * @returns Promise resolving to success message
 */
export async function updatePlanFeature(featureId: number, data: Partial<PlanFeature>) {
  const response = await axiosInstance.put(`${PLAN_CACHE_KEY}/features/${featureId}`, data);
  return response.data as ApiResponse<null>;
}

/**
 * Delete plan feature
 *
 * @method DELETE
 * @param featureId - Feature ID
 * @returns Promise resolving to success message
 */
export async function deletePlanFeature(featureId: number) {
  const response = await axiosInstance.delete(`${PLAN_CACHE_KEY}/features/${featureId}`);
  return response.data as ApiResponse<null>;
}

/**
 * Create subscription
 *
 * @method POST
 * @param data - Subscription data
 * @returns Promise resolving to created subscription
 */
export async function createSubscription(data: CreateSubscriptionInput) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}/subscribe`, data);
  return response.data as ApiResponse<{ insertId: number }>;
}

/**
 * Get user subscriptions
 *
 * @method GET
 * @param userId - User ID
 * @returns Promise resolving to array of subscriptions
 */
export async function getUserSubscriptions(userId: string) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/subscriptions/user/${userId}`);
  return response.data as ApiResponse<Subscription[]>;
}

export async function getSubscriptionsPaged(
  page: number = 0,
  size: number = 10,
  filters?: { status?: string; payment_status?: string }
) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/subscriptions/paged`, {
    params: { page, size, ...filters },
  });
  return response.data as Page<SubscriptionFull>;
}

/**
 * Get subscription by ID
 *
 * @method GET
 * @param subscriptionId - Subscription ID
 * @returns Promise resolving to subscription details
 */
export async function getSubscriptionById(subscriptionId: number) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/subscriptions/${subscriptionId}`);
  return response.data as ApiResponse<Subscription>;
}

/**
 * Update subscription
 *
 * @method PUT
 * @param subscriptionId - Subscription ID
 * @param data - Updated subscription data
 * @returns Promise resolving to success message
 */
export async function updateSubscription(subscriptionId: number, data: UpdateSubscriptionInput) {
  const response = await axiosInstance.put(`${PLAN_CACHE_KEY}/subscriptions/${subscriptionId}`, data);
  return response.data as ApiResponse<null>;
}

/**
 * Get all payments for a subscription (admin)
 */
export async function getSubscriptionPayments(subscriptionId: number) {
  const response = await axiosInstance.get(`${PLAN_CACHE_KEY}/subscriptions/${subscriptionId}/payments`);
  return response.data as ApiResponse<SubscriptionPayment[]>;
}

/**
 * Add a payment to a subscription (admin)
 */
export async function addSubscriptionPayment(
  subscriptionId: number,
  data: { amount_cents: number; paid_at: string; note?: string }
) {
  const response = await axiosInstance.post(`${PLAN_CACHE_KEY}/subscriptions/${subscriptionId}/payments`, data);
  return response.data as ApiResponse<{ id: number }>;
}

/**
 * Delete a payment from a subscription (admin)
 */
export async function deleteSubscriptionPayment(subscriptionId: number, paymentId: number) {
  const response = await axiosInstance.delete(
    `${PLAN_CACHE_KEY}/subscriptions/${subscriptionId}/payments/${paymentId}`
  );
  return response.data as ApiResponse<null>;
}

// React Query helpers
export const planQueryKeys = {
  all: [PLAN_CACHE_KEY] as const,
  lists: () => [...planQueryKeys.all, "list"] as const,
  list: (filters?: { plan_type?: string; is_active?: boolean }) =>
    [...planQueryKeys.lists(), filters] as const,
  details: () => [...planQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...planQueryKeys.details(), id] as const,
  active: () => [...planQueryKeys.all, "active"] as const,
  durations: (planId: string) => [...planQueryKeys.detail(planId), "durations"] as const,
  features: (planId: string) => [...planQueryKeys.detail(planId), "features"] as const,
  subscriptions: () => [...planQueryKeys.all, "subscriptions"] as const,
  userSubscriptions: (userId: string) => [...planQueryKeys.subscriptions(), "user", userId] as const,
};

// Utility function to invalidate plan cache
export function invalidatePlanCache(queryClient: QueryClient, specificKey?: string[]) {
  if (specificKey) {
    queryClient.invalidateQueries({ queryKey: specificKey });
  }
  else {
    queryClient.invalidateQueries({ queryKey: planQueryKeys.all });
  }
}
